import { formatPhoneNumber } from '@/utils/format'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** True if the value is a well-formed email address. */
export function isValidEmail(value) {
  return EMAIL_REGEX.test(String(value ?? '').trim())
}

/** Normalize epoch (ms or seconds) to milliseconds. */
export function normalizeEpochMs(value) {
  if (value == null || value === '') return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.getTime()
  }
  const num = Number(value)
  if (Number.isFinite(num) && num > 0) {
    // Seconds vs milliseconds heuristic.
    return num < 1e12 ? num * 1000 : num
  }
  const text = String(value).trim()
  if (!text) return null
  // DD/MM/YYYY or DD-MM-YYYY or DDMMYYYY
  const slash = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
  if (slash) {
    const day = Number(slash[1])
    const month = Number(slash[2])
    const year = Number(slash[3])
    const d = new Date(year, month - 1, day)
    return Number.isNaN(d.getTime()) ? null : d.getTime()
  }
  const compact = text.match(/^(\d{2})(\d{2})(\d{4})$/)
  if (compact) {
    const day = Number(compact[1])
    const month = Number(compact[2])
    const year = Number(compact[3])
    const d = new Date(year, month - 1, day)
    return Number.isNaN(d.getTime()) ? null : d.getTime()
  }
  const parsed = Date.parse(text)
  return Number.isNaN(parsed) ? null : parsed
}

/** Epoch → DD/MM/YYYY display. */
export function formatEpochAsDdMmYyyy(value) {
  const ms = normalizeEpochMs(value)
  if (ms == null) return ''
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = String(d.getFullYear())
  return `${dd}/${mm}/${yyyy}`
}

/** Epoch → YYYY-MM-DD for `<input type="date">`. */
export function epochToDateInputValue(value) {
  const ms = normalizeEpochMs(value)
  if (ms == null) return ''
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return ''
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/** YYYY-MM-DD → epoch ms (local midnight). */
export function dateInputValueToEpoch(value) {
  const text = String(value ?? '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null
  const d = new Date(`${text}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d.getTime()
}

/** Split stored phone into { dialCode, national } for editors. */
export function splitPhoneNumber(value, fallbackDial = '+91') {
  const raw = String(value ?? '').trim()
  if (!raw) return { dialCode: fallbackDial, national: '' }

  const formatted = formatPhoneNumber(raw, { emptyLabel: '' })
  const match = String(formatted || raw).match(/^\+(\d{1,4})-?(\d+)$/)
  if (match) {
    return { dialCode: `+${match[1]}`, national: match[2] }
  }

  const digits = raw.replace(/\D/g, '')
  if (digits.length > 10) {
    return {
      dialCode: `+${digits.slice(0, digits.length - 10)}`,
      national: digits.slice(-10),
    }
  }
  return { dialCode: fallbackDial, national: digits }
}

/**
 * Combine dial code + national into the canonical stored phone string
 * (+<countrycode><number>, no separator) -- this is what the API validates
 * against (PHONE_NUMBER_REGEX = /^\+\d+$/ server-side), so it must NOT go
 * through formatPhoneNumber, which is display-only and always inserts a
 * dash.
 */
export function joinPhoneNumber(dialCode, national) {
  const code = String(dialCode ?? '').replace(/\D/g, '')
  const number = String(national ?? '').replace(/\D/g, '')
  if (!number) return ''
  return `+${code}${number}`
}

/**
 * Display value for a typed field (DATE → DD/MM/YYYY, PHONE → +CC-number).
 */
export function formatTypedFieldDisplay(value, type) {
  const t = String(type ?? '').trim().toUpperCase()
  if (value == null || value === '' || value === '—') return ''
  if (t === 'DATE') return formatEpochAsDdMmYyyy(value)
  if (t === 'PHONE_NUMBER') {
    return formatPhoneNumber(value, { emptyLabel: '' })
  }
  return String(value).trim()
}

/** Coerce form value to API payload type. */
export function coerceFieldValueForApi(rawValue, type) {
  const t = String(type ?? '').trim().toUpperCase()
  if (rawValue == null || String(rawValue).trim() === '') return null

  if (t === 'NUMBER') {
    const num = Number(rawValue)
    return Number.isNaN(num) ? null : num
  }
  if (t === 'DATE') {
    // Prefer already-epoch numbers; otherwise date input / display strings.
    if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
      return normalizeEpochMs(rawValue)
    }
    const fromInput = dateInputValueToEpoch(rawValue)
    if (fromInput != null) return fromInput
    return normalizeEpochMs(rawValue)
  }
  if (t === 'PHONE_NUMBER') {
    // Canonical form only (+<digits>, no dash) -- same reasoning as
    // joinPhoneNumber above. rawValue here is normally already canonical
    // (set via joinPhoneNumber), this is just a safety net for any other
    // caller that lands raw/dash-formatted input in fieldValues.
    const digits = String(rawValue).replace(/\D/g, '')
    return digits ? `+${digits}` : null
  }
  return String(rawValue).trim()
}
